/**
 * Admin V3 — Notification resend control (AV3 A-5)
 *
 * Per-notification resend affordance for FAILED deliveries. Reuses the canonical
 * endpoint POST /admin/control-tower/notifications/resend (RBAC:
 * admin:notifications:log). No new business logic, no backend change.
 *
 * HONESTY GATE (F14): the backend resend is currently a fake-success stub (it
 * does not actually re-deliver). This control therefore consults
 * isNotificationResendEnabled():
 *   - gate OFF (F14 unresolved, default) → render a DISABLED button with an
 *     explicit reason; NEVER call the endpoint, NEVER show success.
 *   - gate ON  (F14 fixed) → active resend; success/error reflect the real
 *     backend result.
 * This guarantees the UI never falsely indicates a notification was resent.
 */
import React from 'react';
import { Loader2, RotateCcw } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import useAdminMutation from '../../shared/hooks/useAdminMutation';
import usePermissions from '../../shared/hooks/usePermissions';
import { useToast } from '../../shared/hooks/useToast';
import { t, DEFAULT_LOCALE } from '../../i18n';
import { isNotificationResendEnabled } from './notificationResend';

const PERM_NOTIFICATIONS = 'admin:notifications:log';

const NotificationResendControl = ({ notification, onDone, locale = DEFAULT_LOCALE }) => {
  const { can } = usePermissions();
  const m = useAdminMutation('/admin/control-tower/notifications/resend', 'post');
  const { showSuccess, showError } = useToast();

  // Backend authoritative; hide the affordance entirely without the permission.
  if (!can(PERM_NOTIFICATIONS)) return null;

  const notificationId = notification?.id;
  const notificationType = (notification?.channel || notification?.type || '').toLowerCase();
  const enabled = isNotificationResendEnabled();

  // F14 honesty gate: while the backend cannot truly re-deliver, present a
  // disabled control with an explicit reason. No endpoint call, no false success.
  if (!enabled) {
    return (
      <div className="mt-2 flex items-center gap-2">
        <Button
          variant="outline"
          size="sm"
          disabled
          className="h-7 px-2 text-[12px] opacity-60"
          data-testid="adminv3-notification-resend-disabled"
        >
          <RotateCcw className="h-3.5 w-3.5 mr-1.5" />{t('operations.notifications.resend.label', locale)}
        </Button>
        <span className="text-[11px] text-slate-400" data-testid="adminv3-notification-resend-disabled-reason">
          {t('operations.notifications.resend.disabledReason', locale)}
        </span>
      </div>
    );
  }

  const submit = () => {
    if (!notificationId || !notificationType) return;
    m.mutate(
      { notification_id: notificationId, notification_type: notificationType },
      {
        onSuccess: () => {
          showSuccess(t('operations.notifications.resend.ok', locale));
          if (onDone) onDone();
        },
        onError: (e) => showError(e?.response?.data?.detail || t('operations.notifications.resend.error', locale)),
      },
    );
  };

  return (
    <div className="mt-2">
      <Button
        variant="outline"
        size="sm"
        onClick={submit}
        disabled={m.loading}
        className="h-7 px-2 text-[12px] border-amber-200 text-amber-700 hover:bg-amber-50"
        data-testid="adminv3-notification-resend"
      >
        {m.loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="h-3.5 w-3.5 mr-1.5" />}
        {t('operations.notifications.resend.label', locale)}
      </Button>
    </div>
  );
};

export default NotificationResendControl;
